import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiFetch } from "@/api/client";
import {
  Users as UsersIcon,
  UserPlus,
  Shield,
  Loader2,
  Trash2,
  Building2,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Check,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EntraUser {
  entraObjectId: string;
  displayName: string;
  email: string;
  jobTitle: string | null;
}

interface TenantRef {
  companyId: string;
  companyName: string;
}

interface PlatformMember {
  id: string;
  email: string;
  displayName: string | null;
  entraObjectId: string | null;
  role: string;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
  tenants: TenantRef[];
}

interface Company {
  id: string;
  name: string;
}

type PlatformRole = "standard" | "admin" | "platform_admin";

const ROLE_LABELS: Record<PlatformRole, string> = {
  standard: "Standard",
  admin: "Admin",
  platform_admin: "Platform Admin",
};

const ROLE_COLORS: Record<PlatformRole, string> = {
  standard: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  admin: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  platform_admin: "bg-purple-500/10 text-purple-600 border-purple-500/20",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Users() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);

  // Fetch platform members
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["platform-members"],
    queryFn: () => apiFetch<PlatformMember[]>("/admin/platform-members"),
  });

  // Fetch companies (for tenant picker)
  const { data: companiesData } = useQuery({
    queryKey: ["companies"],
    queryFn: () => apiFetch<Company[]>("/companies"),
  });

  return (
    <CenterPanel>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
            <UsersIcon size={24} />
            Platform Users
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Add users from your Entra directory and control their platform access.
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
        >
          <UserPlus size={16} className="mr-2" />
          Add User
        </Button>
      </div>

      {showAddForm && (
        <AddMemberForm
          companies={companiesData ?? []}
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false);
            queryClient.invalidateQueries({ queryKey: ["platform-members"] });
          }}
        />
      )}

      {/* Members List */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UsersIcon size={16} />
            Platform Members
            {members && (
              <Badge variant="outline" className="ml-auto">
                {members.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-text-muted" />
            </div>
          ) : members && members.length > 0 ? (
            <div className="divide-y divide-border">
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  companies={companiesData ?? []}
                  isExpanded={expandedMember === member.id}
                  onToggle={() =>
                    setExpandedMember(expandedMember === member.id ? null : member.id)
                  }
                />
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-text-muted">
              No platform members yet. Add users from your Entra directory.
            </p>
          )}
        </CardContent>
      </Card>
    </CenterPanel>
  );
}

// ---------------------------------------------------------------------------
// Add Member Form
// ---------------------------------------------------------------------------

function AddMemberForm({
  companies,
  onClose,
  onSuccess,
}: {
  companies: Company[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<EntraUser | null>(null);
  const [role, setRole] = useState<PlatformRole>("standard");
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);

  const { data: entraUsers, isLoading: entraLoading, error: entraError } = useQuery({
    queryKey: ["entra-users"],
    queryFn: () => apiFetch<EntraUser[]>("/admin/platform-members/entra-users"),
  });

  const createMember = useMutation({
    mutationFn: (body: {
      email: string;
      displayName: string;
      entraObjectId: string;
      role: string;
      tenantIds: string[];
    }) =>
      apiFetch<PlatformMember>("/admin/platform-members", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess,
  });

  const filteredUsers = entraUsers?.filter(
    (u) =>
      u.displayName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    createMember.mutate({
      email: selectedUser.email,
      displayName: selectedUser.displayName,
      entraObjectId: selectedUser.entraObjectId,
      role,
      tenantIds: role === "platform_admin" ? [] : selectedTenants,
    });
  };

  const toggleTenant = (companyId: string) => {
    setSelectedTenants((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId],
    );
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-base">Add Platform User from Entra</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Entra User Picker */}
          <div>
            <label className="mb-1 block text-xs text-text-muted">
              Select User from Entra Directory
            </label>
            {selectedUser ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
                <div>
                  <span className="text-sm font-medium">{selectedUser.displayName}</span>
                  <span className="ml-2 text-sm text-text-secondary">
                    {selectedUser.email}
                  </span>
                  {selectedUser.jobTitle && (
                    <span className="ml-2 text-xs text-text-muted">
                      ({selectedUser.jobTitle})
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedUser(null)}
                >
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                  />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-border"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                  {entraLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 size={16} className="animate-spin text-text-muted" />
                      <span className="ml-2 text-sm text-text-muted">
                        Loading Entra directory...
                      </span>
                    </div>
                  ) : entraError ? (
                    <p className="px-3 py-4 text-sm text-red-500">
                      Failed to load Entra users: {(entraError as Error).message}
                    </p>
                  ) : filteredUsers && filteredUsers.length > 0 ? (
                    filteredUsers.map((u) => (
                      <button
                        key={u.entraObjectId}
                        type="button"
                        onClick={() => setSelectedUser(u)}
                        className="w-full px-3 py-2 text-left hover:bg-muted/50 border-b border-border last:border-b-0 transition-colors"
                      >
                        <span className="text-sm font-medium text-text-primary">
                          {u.displayName}
                        </span>
                        <span className="ml-2 text-sm text-text-secondary">{u.email}</span>
                        {u.jobTitle && (
                          <span className="ml-2 text-xs text-text-muted">
                            {u.jobTitle}
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-4 text-sm text-text-muted">
                      {search ? "No matching users" : "No users found"}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Role Selector */}
          <div>
            <label className="mb-1 block text-xs text-text-muted">Platform Role</label>
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as PlatformRole)}
            >
              <option value="standard">Standard - View assigned tenants</option>
              <option value="admin">Admin - Manage assigned tenants</option>
              <option value="platform_admin">
                Platform Admin - Full access to all tenants
              </option>
            </select>
          </div>

          {/* Tenant Access (not shown for platform_admin) */}
          {role !== "platform_admin" && (
            <div>
              <label className="mb-1 block text-xs text-text-muted">
                Tenant Access
              </label>
              <p className="mb-2 text-xs text-text-muted">
                Select which tenants this user can access.
              </p>
              {companies.length > 0 ? (
                <div className="space-y-1 max-h-40 overflow-y-auto rounded-md border border-border p-2">
                  {companies.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(c.id)}
                        onChange={() => toggleTenant(c.id)}
                        className="rounded border-border"
                      />
                      <Building2 size={14} className="text-text-muted" />
                      <span className="text-sm">{c.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-text-muted">No tenants available</p>
              )}
            </div>
          )}

          {createMember.error && (
            <p className="text-sm text-red-500">
              {(createMember.error as Error).message}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={!selectedUser || createMember.isPending}
            >
              {createMember.isPending && (
                <Loader2 size={14} className="mr-2 animate-spin" />
              )}
              Add Platform User
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Member Row (with expand for tenant management)
// ---------------------------------------------------------------------------

function MemberRow({
  member,
  companies,
  isExpanded,
  onToggle,
}: {
  member: PlatformMember;
  companies: Company[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const queryClient = useQueryClient();
  const [editRole, setEditRole] = useState<PlatformRole | null>(null);
  const [editTenants, setEditTenants] = useState<string[] | null>(null);

  const updateRole = useMutation({
    mutationFn: (role: string) =>
      apiFetch(`/admin/platform-members/${member.id}`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      setEditRole(null);
      queryClient.invalidateQueries({ queryKey: ["platform-members"] });
    },
  });

  const updateTenants = useMutation({
    mutationFn: (tenantIds: string[]) =>
      apiFetch(`/admin/platform-members/${member.id}/tenants`, {
        method: "PUT",
        body: JSON.stringify({ tenantIds }),
      }),
    onSuccess: () => {
      setEditTenants(null);
      queryClient.invalidateQueries({ queryKey: ["platform-members"] });
    },
  });

  const removeMember = useMutation({
    mutationFn: () =>
      apiFetch(`/admin/platform-members/${member.id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-members"] });
    },
  });

  const role = member.role as PlatformRole;
  const currentTenantIds = member.tenants.map((t) => t.companyId);
  const activeTenants = editTenants ?? currentTenantIds;

  const toggleTenant = (companyId: string) => {
    const current = editTenants ?? currentTenantIds;
    const next = current.includes(companyId)
      ? current.filter((id) => id !== companyId)
      : [...current, companyId];
    setEditTenants(next);
  };

  return (
    <div className="py-3">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text-primary truncate">
              {member.displayName || member.email}
            </span>
            {member.displayName && (
              <span className="text-sm text-text-secondary truncate">
                {member.email}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {editRole ? (
              <div className="flex items-center gap-1">
                <select
                  className="rounded border border-border bg-background px-2 py-0.5 text-xs"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as PlatformRole)}
                >
                  <option value="standard">Standard</option>
                  <option value="admin">Admin</option>
                  <option value="platform_admin">Platform Admin</option>
                </select>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1"
                  onClick={() => updateRole.mutate(editRole)}
                  disabled={updateRole.isPending}
                >
                  <Check size={12} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1"
                  onClick={() => setEditRole(null)}
                >
                  <X size={12} />
                </Button>
              </div>
            ) : (
              <Badge
                variant="outline"
                className={`gap-1 text-xs cursor-pointer ${ROLE_COLORS[role] || ""}`}
                onClick={() => setEditRole(role)}
              >
                <Shield size={10} />
                {ROLE_LABELS[role] || role}
              </Badge>
            )}
            {role !== "platform_admin" && member.tenants.length > 0 && (
              <span className="flex items-center gap-1 text-xs text-text-muted">
                <Building2 size={10} />
                {member.tenants.length} tenant{member.tenants.length !== 1 ? "s" : ""}
              </span>
            )}
            {role === "platform_admin" && (
              <span className="text-xs text-text-muted">All tenants</span>
            )}
            {!member.userId && (
              <Badge variant="outline" className="text-xs text-text-muted">
                Not yet signed in
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {role !== "platform_admin" && (
            <Button size="sm" variant="ghost" onClick={onToggle}>
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => removeMember.mutate()}
            disabled={removeMember.isPending}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Expanded: Tenant Access Management */}
      {isExpanded && role !== "platform_admin" && (
        <div className="mt-3 ml-2 pl-3 border-l-2 border-border">
          <p className="text-xs font-medium text-text-muted mb-2">Tenant Access</p>
          {companies.length > 0 ? (
            <div className="space-y-1">
              {companies.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={activeTenants.includes(c.id)}
                    onChange={() => toggleTenant(c.id)}
                    className="rounded border-border"
                  />
                  <Building2 size={12} className="text-text-muted" />
                  <span className="text-sm">{c.name}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-xs text-text-muted">No tenants available</p>
          )}
          {editTenants !== null && (
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                onClick={() => updateTenants.mutate(editTenants)}
                disabled={updateTenants.isPending}
              >
                {updateTenants.isPending && (
                  <Loader2 size={12} className="mr-1 animate-spin" />
                )}
                Save Tenant Access
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditTenants(null)}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
